/**
 * Wire types for the full-catalog export (`/v1/exports/catalog/**` on
 * agentic-export-service; contract in that repo's `docs/catalog-export-api.md`).
 * Mirrors `catalog_schemas.py` / `catalog/models.py` verbatim. The bodies are
 * STRICT server-side — an unknown key anywhere is a 400 — so nothing extra may
 * ride along here.
 */

import type {
  ExportConnector,
  ExportFilters,
  ExportFormat,
  ExportOptions,
  ExportTemplatePreview,
  ExportTemplatePreviewRow,
  ExportTenantContext,
} from '../../types/export.types';

/** Which batches a catalog starts from, before any explicit pick. */
export type CatalogBatchPreset = 'RELEASED' | 'ALL_FINISHED' | 'ALL';

/** How ONE version per SKU is chosen when several batches enriched it. */
export type CatalogVersionRule = 'LATEST' | 'PRODUCT_COHERENT' | 'ALL_VERSIONS';

export type CatalogCompression = 'NONE' | 'GZIP' | 'ZIP';

export type CatalogGroupBy = 'NONE' | 'MARKET';

/** Which of the subsidiary's batches contribute rows. Omitted keys = server defaults. */
export interface CatalogBatchSelection {
  preset?: CatalogBatchPreset;
  /** Exactly these batches (`enrichment_job.workflow_id`); the preset is then ignored. */
  includeBatchIds?: string[];
  /** Never these batches, whatever else matches. */
  excludeBatchIds?: string[];
  includeArchived?: boolean;
  /** Only batches created at/after this instant (ISO). Ignored with `includeBatchIds`. */
  createdFrom?: string;
  /** Only batches created before this instant (ISO). Ignored with `includeBatchIds`. */
  createdTo?: string;
  /** Highest priority first; unlisted batches follow, newest first. */
  priority?: string[];
}

/** Force one batch's version for a SKU or a whole product (Part 2 UI). */
export type CatalogVersionPin =
  | { externalId: string; itemGroupId?: undefined; batchId: string }
  | { externalId?: undefined; itemGroupId: string; batchId: string };

/**
 * "Only products live in Google Merchant Center" — drops SKUs the GMC feed no longer carries
 * (deleted/disapproved) from the file. GMC only (400 on META). Re-pulls the feed before
 * building when the last pull is older than `maxAgeHours`; omitting this whole block never
 * touches ClickHouse (no liveness filtering, no extra cost).
 */
export interface CatalogLiveness {
  source: 'GMC_CATALOG';
  connectionId?: string | null;
  includeDisapproved?: boolean;
  /** 1–8760; omitted = the service's own default. Exceeding it is a 409 (stale sync). */
  maxAgeHours?: number | null;
  /** The COMPLETED pull to judge against (the connection's `lastSyncRunId`). Without it the
   *  service reads the newest pull, which can still be writing. Never saved on a profile:
   *  each run pins its own. */
  syncId?: string | null;
}

/** Everything that decides a catalog's rows. Only `connector` is required. */
export interface CatalogSpec {
  connector: ExportConnector;
  batches?: CatalogBatchSelection;
  versionRule?: CatalogVersionRule;
  pins?: CatalogVersionPin[];
  exclude?: { externalIds?: string[]; itemGroupIds?: string[] };
  /** Only these SKUs (`external_id`); absent = every SKU. */
  externalIds?: string[];
  liveness?: CatalogLiveness;
}

/** How the rows become files. Changes the files, never which rows are in them. */
export interface CatalogPackaging {
  compression: CatalogCompression;
  /** Most rows per file (1,000–10,000,000); null = one file (per folder). */
  splitRows: number | null;
  /** `MARKET`: one folder per GMC market. GMC only (400 on META). */
  groupBy: CatalogGroupBy;
  /** The export fails rather than write more files than this (1–10,000). */
  maxFiles: number;
  /**
   * `null` = the service's own default name. 1–300 chars; tokens `{subsidiary_code}
   * {connector} {date} {datetime} {market} {part}` (no file extension — the service appends
   * it). Omitting `{market}`/`{part}` when needed to disambiguate is auto-appended
   * server-side; any other unknown token is a 400 at trigger time (surfaced live first by
   * `nameWarnings`, `~/features/exports/utils/file-name-tokens.ts`).
   */
  fileNamePattern: string | null;
  /** IANA zone governing `{date}`/`{datetime}`; ignored without `fileNamePattern`. Default `UTC`. */
  fileNameTimezone: string;
  /** Leave out every column no row fills (identifiers stay) — decided by the service once the
   *  whole file is rendered. In the draft, absent = on (the default) and `false` = off; on the
   *  wire it is sent only as `true`, since an export service without it (before
   *  agentic-export-service#121) refuses unknown packaging fields. */
  dropEmptyColumns?: boolean;
}

/** Catalog filters: the batch ones minus `productIds`, which the catalog refuses. */
export type CatalogFilters = Omit<ExportFilters, 'productIds' | 'fieldFilters'>;

/**
 * Body shared by summary / estimate / preview / trigger. Inline `catalog` only
 * in this UI so far; `selectionId` (saved selections) arrives with Part 3.
 */
export interface CatalogExportRequest {
  tenantContext: ExportTenantContext;
  catalog: CatalogSpec;
  template?: string;
  fields?: string[] | null;
  format?: ExportFormat;
  filters?: CatalogFilters;
  options?: ExportOptions;
  /**
   * The grid's narrowing, sent on estimate / trigger when the user exports "what
   * the grid shows" (≥ 3-char search; filters on the shipped version). Needs the
   * export service's catalog sort/filter update; older services refuse the keys.
   */
  search?: string;
  columnFilters?: CatalogColumnFilters | null;
}

/** `POST /catalog/trigger` body. */
export interface CatalogTriggerRequest extends CatalogExportRequest {
  packaging?: CatalogPackaging;
}

/** One batch in the picker (`GET /catalog/batches`). */
export interface CatalogBatch {
  batchId: string;
  name: string | null;
  /** Effective status: `pipeline_status` for a scheduled pipeline, else `status`. */
  status: string;
  source: string | null;
  createdAt: string;
  archivedAt: string | null;
  /** Who started the batch — a user's email, or the service name for a scheduled run. Absent
   *  on an export service before #121. */
  createdBy?: string | null;
  /** DELIVERING or COMPLETED — approved and released to the customer. */
  released: boolean;
  /** Whether the requested preset takes this batch. */
  selected: boolean;
  rows: number;
  successfulRows: number;
  /**
   * Column keys the batch asked Lily to enrich (its trigger's field selection); `null` when the
   * batch recorded none. Absent from a service predating it.
   */
  enrichedFields?: string[] | null;
  /** The run's root workflow id — the enrichment-validation key. Absent from older services. */
  rootWorkflowId?: string;
}

/** `GET /catalog/batches`. At most 10,000 batches; `truncated` says there were more. */
export interface CatalogBatchesResponse {
  connector: ExportConnector;
  batches: CatalogBatch[];
  selectedCount: number;
  totalCount: number;
  truncated: boolean;
}

/** How much of the catalog one batch supplies. */
export interface CatalogBatchContribution {
  batchId: string;
  name: string | null;
  status: string | null;
  createdAt: string | null;
  /** SKUs whose shipped version comes from this batch. */
  winners: number;
  versions: number;
}

/** `POST /catalog/summary` — the metrics bar. Cached 5 min server-side. */
export interface CatalogSummary {
  batchesSelected: number;
  versions: number;
  /** Rows the file will contain (before `skipEmptyRows`). */
  skus: number;
  products: number;
  duplicatesCollapsed: number;
  multiVersionSkus: number;
  /** Products whose SKUs ship from more than one batch — what the version rule decides. */
  mixedBatchProducts: number;
  mixedBatchSkus: number;
  /** SKUs with no successful version in the selection (not in the file). */
  neverSucceeded: number;
  /** SKUs whose newest version failed, so an older successful one ships. */
  fellBack: number;
  batches: CatalogBatchContribution[];
  /** GMC market (`en~US`); `''` = the id carries no market. */
  markets: { market: string; skus: number }[];
  categories: { category: string; skus: number }[];
  freshness: {
    last7Days: number;
    last30Days: number;
    last90Days: number;
    older: number;
    oldest?: string | null;
    newest?: string | null;
  };
  /** Present only when the request carried `catalog.liveness` — the GMC-feed cross-check. */
  liveness?: {
    /** `null` if no sync was ever found for the connection. */
    lastSyncedAt: string | null;
    /** The live-set size (not intersected with this selection). */
    liveSkus: number;
    /** This selection's SKUs the liveness filter dropped. */
    droppedNotLive: number;
    /** The freshness limit applied (export service #121+); absent on an older service. */
    maxAgeHours?: number | null;
    /** The pull is older than `maxAgeHours`: the page still reads it, the export refuses. */
    stale?: boolean;
    /** Non-deleted rows in the latest GMC pull (export service 2026-10-26+). */
    pulledSkus?: number;
    /** Of those, how many GMC disapproved — why "0 live" can follow a fresh pull. */
    disapprovedSkus?: number;
    /** The request's `includeDisapproved`, echoed back. */
    includeDisapproved?: boolean;
  };
}

/** The summary's GMC liveness facts, when the request carried `catalog.liveness`. */
export type CatalogLivenessFacts = NonNullable<CatalogSummary['liveness']>;

/** Where a catalog row came from (`rows[].meta`). */
export interface CatalogRowMeta {
  externalId: string;
  itemGroupId: string | null;
  market: string | null;
  batchId: string;
  batchName: string | null;
  batchCreatedAt: string | null;
  enrichedAt: string | null;
  /** Candidate versions of this SKU; > 1 offers "see versions". */
  versions: number;
  /** Post-enrichment content score of the shipped version — the number the row rules'
   *  score filter reads. `null`/absent when unscored (or an older API). */
  contentScore?: number | null;
}

/** A rendered catalog row: the template preview row plus its provenance. */
export interface CatalogRow extends ExportTemplatePreviewRow {
  meta?: CatalogRowMeta | null;
}

/** `POST /catalog/preview` — the template preview plus row provenance. */
export interface CatalogPreview extends ExportTemplatePreview {
  rows: CatalogRow[];
}

/** `sourceWorkflowId` catalog export rows carry, for history and the active-export check. */
export function catalogSourceId(connector: ExportConnector): string {
  return `catalog:${connector}`;
}

/** Browse pages SKUs (default) or whole products (`item_group_id`, variants together). */
export type CatalogBrowseGrouping = 'SKU' | 'PRODUCT';

/** `POST /catalog/browse` body. `search` needs ≥ 3 characters (400 otherwise). */
export interface CatalogBrowseRequest extends CatalogExportRequest {
  /** 1–200 SKUs, or ≤ 50 products with `groupBy: PRODUCT`. */
  pageSize: number;
  cursor?: string | null;
  /** 1-based page to jump to instead of `cursor` (SKU paging only) — numbered pagination. */
  page?: number;
  search?: string;
  groupBy?: CatalogBrowseGrouping;
  /**
   * Sort the SHIPPED versions by a filterable column or `meta.enrichedAt` / `meta.batchName` /
   * `meta.versions` / `meta.market`. SKU paging only.
   */
  sort?: CatalogBrowseSort | null;
  /** Case-insensitive substring per filterable column / `meta.batchName` / `meta.market`. SKU paging only. */
  columnFilters?: CatalogColumnFilters | null;
}

/** `sort` on a catalog browse request. */
export interface CatalogBrowseSort {
  key: string;
  desc: boolean;
}

/** A browse row's provenance, plus `productSkus` when paging by product. */
export interface CatalogBrowseRowMeta extends CatalogRowMeta {
  productSkus?: number;
}

/** `POST /catalog/browse` — one keyset page. `total` counts what the page counts. */
export interface CatalogBrowsePage {
  header: string[];
  rows: (ExportTemplatePreviewRow & { meta?: CatalogBrowseRowMeta | null })[];
  rowCount: number;
  total: number;
  nextCursor: string | null;
  connectorCode: ExportConnector;
  groupBy?: CatalogBrowseGrouping;
}

/** `POST /catalog/versions` body: SKUs (≤ 100) and/or products (≤ 20). */
export interface CatalogVersionsRequest extends CatalogExportRequest {
  externalIds?: string[];
  itemGroupIds?: string[];
}

/** One version of one SKU, in one batch. */
export interface CatalogVersion {
  externalId: string;
  itemGroupId: string;
  market: string | null;
  batchId: string;
  batchName: string | null;
  batchStatus: string | null;
  workflowId: string;
  productId: string;
  phase: string;
  /** Lower wins: batch priority/recency, full pass before sample pass. */
  rank: number;
  success: boolean;
  /** Passes the request's filters (e.g. `successOnly`). */
  eligible: boolean;
  /** The version this selection ships for the SKU. */
  shipped: boolean;
  enrichedAt: string | null;
  cells: string[];
}

/** `POST /catalog/versions`. */
export interface CatalogVersionsResponse {
  header: string[];
  versions: CatalogVersion[];
  truncated: boolean;
}

/** `POST /catalog/facets` body: the catalog request plus the keys to list values for. */
export interface CatalogFacetsRequest extends CatalogExportRequest {
  keys: string[];
  search?: string;
  columnFilters?: CatalogColumnFilters | null;
}

/** Distinct shipped values of one key, most frequent first (top 100). */
export interface CatalogFacet {
  values: { value: string; count: number }[];
  truncated: boolean;
}

/** `POST /catalog/facets`. */
export interface CatalogFacetsResponse {
  facets: Record<string, CatalogFacet>;
}

/** "Has a value" (`present: true`) / "Is empty" (`present: false`) on the shipped version. */
export interface CatalogPresenceFilter {
  present: boolean;
}

/**
 * One grid filter: a string is a case-insensitive "contains" match; a list is
 * "any of these values", matched exactly (multi-select); `{present}` tests for a value.
 * All on the shipped version.
 */
export type CatalogFilterValue = string | string[] | CatalogPresenceFilter;

/** `columnFilters` on catalog requests: filter key → its value. */
export type CatalogColumnFilters = Record<string, CatalogFilterValue>;

/** Whether a filter value narrows anything (non-blank text, or a non-empty list). */
export function isActiveFilter(value: CatalogFilterValue | undefined): boolean {
  if (value === undefined) return false;
  if (isPresenceFilter(value)) return true;
  return Array.isArray(value) ? value.length > 0 : value.trim() !== '';
}

/** Whether a filter value is a presence test rather than text or a value list. */
export function isPresenceFilter(
  value: CatalogFilterValue | undefined,
): value is CatalogPresenceFilter {
  return typeof value === 'object' && !Array.isArray(value);
}

/** One batch's rows in the file (parallel lists, file order) — `POST /catalog/validation-targets`. */
export interface CatalogValidationBatchTarget {
  batchId: string;
  name: string | null;
  /** The run's validation key (pipeline id, else the job id). */
  rootWorkflowId: string;
  productIds: string[];
  externalIds: string[];
}

/** Exactly the rows the selection ships, grouped by batch. `truncated` past 50 000 rows. */
export interface CatalogValidationTargets {
  total: number;
  truncated: boolean;
  batches: CatalogValidationBatchTarget[];
}
